import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  Zap, Trophy, Search, ChevronDown, RotateCcw,
  Sparkles, TrendingUp, Users, Star, Shield, X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { usePredictionUI } from '../predictions/usePredictionUI';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { predictionsAPI } from '../predictions/services/predictionsApi';
import { searchAPI, sportTypeAPI } from '../services/api';
import {
  PlayerAvatar, ConfidenceBadge, ProbabilityBar,
  PredictionResultCard as PredictionResult, PredictingLoader,
} from '../predictions/components/PredictionResultCard';

// ── ATP top players ──────────────────────────────────────────────────────────
const ATP_PLAYERS = [
  { id: 'atp:S0AG', name: 'Jannik Sinner',          country: '🇮🇹', rank: 1 },
  { id: 'atp:A0E2', name: 'Carlos Alcaraz',          country: '🇪🇸', rank: 2 },
  { id: 'atp:D643', name: 'Novak Djokovic',          country: '🇷🇸', rank: 3 },
  { id: 'atp:Z355', name: 'Alexander Zverev',        country: '🇩🇪', rank: 4 },
  { id: 'atp:MM58', name: 'Daniil Medvedev',         country: '🇷🇺', rank: 5 },
  { id: 'atp:TE51', name: 'Stefanos Tsitsipas',      country: '🇬🇷', rank: 6 },
  { id: 'atp:RH16', name: 'Casper Ruud',             country: '🇳🇴', rank: 7 },
  { id: 'atp:HB71', name: 'Hubert Hurkacz',          country: '🇵🇱', rank: 8 },
  { id: 'atp:AG37', name: 'Felix Auger-Aliassime',   country: '🇨🇦', rank: 9 },
  { id: 'atp:RE44', name: 'Andrey Rublev',           country: '🇷🇺', rank: 10 },
  { id: 'atp:FB98', name: 'Taylor Fritz',            country: '🇺🇸', rank: 11 },
  { id: 'atp:DH58', name: 'Alex de Minaur',          country: '🇦🇺', rank: 12 },
  { id: 'atp:BK40', name: 'Matteo Berrettini',       country: '🇮🇹', rank: 13 },
  { id: 'atp:D875', name: 'Grigor Dimitrov',         country: '🇧🇬', rank: 14 },
  { id: 'atp:SU55', name: 'Denis Shapovalov',        country: '🇨🇦', rank: 15 },
  { id: 'atp:KE29', name: 'Karen Khachanov',         country: '🇷🇺', rank: 16 },
  { id: 'atp:N771', name: 'Cameron Norrie',          country: '🇬🇧', rank: 17 },
  { id: 'atp:TB69', name: 'Dominic Thiem',           country: '🇦🇹', rank: 18 },
  { id: 'atp:N409', name: 'Rafael Nadal',            country: '🇪🇸', rank: 19 },
  { id: 'atp:F324', name: 'Roger Federer',           country: '🇨🇭', rank: 20 },
  { id: 'atp:MC10', name: 'Andy Murray',             country: '🇬🇧', rank: 21 },
  { id: 'atp:W367', name: 'Stan Wawrinka',           country: '🇨🇭', rank: 22 },
];

// ── Featured professional rivalries ─────────────────────────────────────────
const FEATURED_MATCHUPS = [
  {
    id: 'djok-alc',
    label: 'Next Gen Final',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:D643'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:A0E2'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'HOT',
    badgeColor: 'bg-orange-500',
  },
  {
    id: 'sin-med',
    label: 'AO 2024 Final',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:S0AG'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:MM58'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'RIVALRY',
    badgeColor: 'bg-purple-500',
  },
  {
    id: 'nad-fed',
    label: 'Greatest Rivalry',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:N409'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:F324'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'LEGEND',
    badgeColor: 'bg-amber-500',
  },
  {
    id: 'djok-nad',
    label: 'Paris Masters',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:D643'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:N409'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'CLASSIC',
    badgeColor: 'bg-sky-500',
  },
  {
    id: 'alc-zve',
    label: 'Roland Garros SF',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:A0E2'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:Z355'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'TRENDING',
    badgeColor: 'bg-green-500',
  },
  {
    id: 'sin-alc',
    label: 'Future #1 Battle',
    player1: ATP_PLAYERS.find(p => p.id === 'atp:S0AG'),
    player2: ATP_PLAYERS.find(p => p.id === 'atp:A0E2'),
    sportId: 7,
    sport: 'Tennis',
    badge: 'EPIC',
    badgeColor: 'bg-rose-500',
  },
];

// ── Player dropdown (shared) ──────────────────────────────────────────────────
function PlayerSelect({ label, value, onChange, options, placeholder }) {
  const ui = usePredictionUI();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(p =>
    p.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <p className={`text-xs font-semibold ${ui.label} uppercase tracking-widest mb-1.5`}>{label}</p>
      <button
        onClick={() => { setOpen(o => !o); setQ(''); }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl ${ui.surfaceHi} border ${ui.borderHi} hover:border-blue-400/50 transition-all text-left cursor-pointer`}
      >
        {value ? (
          <>
            <PlayerAvatar name={value.name} id={value.id} size="sm" />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${ui.title} truncate`}>{value.name}</p>
              <p className={`text-xs ${ui.label}`}>{value.country} {value.rank ? `· #${value.rank}` : ''}</p>
            </div>
          </>
        ) : (
          <span className={`text-sm ${ui.dim} flex-1`}>{placeholder}</span>
        )}
        <ChevronDown size={14} className={`${ui.label} transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute z-50 mt-1 w-full rounded-xl ${ui.popover} border shadow-2xl overflow-hidden`}>
          <div className={`p-2 border-b ${ui.border}`}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${ui.surface}`}>
              <Search size={13} className={ui.faint} />
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search player..."
                className={`flex-1 bg-transparent text-sm ${ui.title} ${ui.placeholder} outline-none`}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-56">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => { onChange(p); setOpen(false); setQ(''); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 ${ui.hoverSurface} transition-colors text-left ${value?.id === p.id ? ui.surface : ''}`}
              >
                <PlayerAvatar name={p.name} id={p.id} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${ui.title} truncate`}>{p.name}</p>
                  <p className={`text-xs ${ui.faint}`}>{p.country}{p.rank ? ` · #${p.rank}` : ''}</p>
                </div>
                {value?.id === p.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className={`py-6 text-center text-sm ${ui.faint}`}>No players found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Kynaix player search ──────────────────────────────────────────────────────
function KynaixPlayerSearch({ label, value, onChange }) {
  const ui = usePredictionUI();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchAPI.search(val, { types: 'user', limit: 8, semantic: false });
        const users = res?.results?.user?.items || [];
        setResults(users.map(u => ({
          id: u.id,
          name: u.name || 'Unknown',
          username: u.userHandle,
          avatar: u.profileImage,
        })));
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  return (
    <div className="relative" ref={ref}>
      <p className={`text-xs font-semibold ${ui.label} uppercase tracking-widest mb-1.5`}>{label}</p>
      {value ? (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${ui.surface} border ${ui.border}`}>
          <PlayerAvatar name={value.name} id={value.id} size="sm" />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${ui.title} truncate`}>{value.name}</p>
            {value.username && <p className={`text-xs ${ui.faint}`}>@{value.username}</p>}
          </div>
          <button onClick={() => { onChange(null); setQ(''); }} className={`p-1 rounded-lg ${ui.hoverSurface} ${ui.faint} ${ui.hoverDim} transition-colors`}>
            <X size={13} />
          </button>
        </div>
      ) : (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${ui.surfaceHi} border ${ui.borderHi} ${ui.hoverBorder} focus-within:border-blue-400/60 transition-colors cursor-text`}>
          <Search size={14} className={`${ui.faint} flex-shrink-0`} />
          <input
            value={q}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => q.length >= 2 && results.length > 0 && setOpen(true)}
            placeholder="Search Kynaix players..."
            className={`flex-1 bg-transparent text-sm ${ui.title} ${ui.placeholder} outline-none`}
          />
          {searching && <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
        </div>
      )}

      {open && results.length > 0 && !value && (
        <div className={`absolute z-50 mt-1 w-full rounded-xl ${ui.popover} border shadow-2xl overflow-hidden`}>
          <div className="overflow-y-auto max-h-56" style={{ scrollbarWidth: 'thin' }}>
            {results.map(u => (
              <button
                key={u.id}
                onClick={() => { onChange(u); setOpen(false); setQ(''); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 ${ui.hoverSurface} transition-colors text-left`}
              >
                <PlayerAvatar name={u.name} id={u.id} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${ui.title} truncate`}>{u.name}</p>
                  {u.username && <p className={`text-xs ${ui.faint}`}>@{u.username}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sport emoji map ───────────────────────────────────────────────────────────
const SPORT_EMOJI = {
  tennis: '🎾', basketball: '🏀', volleyball: '🏐', pickleball: '🏓',
  cricket: '🏏', soccer: '⚽', football: '🏈', baseball: '⚾',
  badminton: '🏸', squash: '🎯',
};
function sportEmoji(name = '') {
  return SPORT_EMOJI[name.toLowerCase()] || '🏅';
}

// ── Coming Soon card ─────────────────────────────────────────────────────────
function ComingSoonCard({ sport, context }) {
  const ui = usePredictionUI();
  const label = context === 'Kynaix'
    ? `Kynaix ${sport.name} predictions`
    : `${sport.name} pro predictions`;
  const detail = context === 'Kynaix'
    ? `We haven't indexed Kynaix platform ${sport.name} match history yet.`
    : `We haven't indexed professional ${sport.name} match data yet.`;
  return (
    <div className={`p-10 rounded-2xl ${ui.panel} border ${ui.borderSoft} flex flex-col items-center gap-4 text-center`}>
      <div className="text-6xl">{sportEmoji(sport.name)}</div>
      <div>
        <p className={`${ui.title} font-bold text-xl capitalize`}>{label}</p>
        <span className={`inline-block mt-2 px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-xs font-black ${ui.isDark ? 'text-blue-300' : 'text-blue-700'} uppercase tracking-widest`}>
          Coming Soon
        </span>
      </div>
      <p className={`text-xs ${ui.faint} max-w-xs leading-relaxed`}>
        {detail} Predictions will be available once enough match history is indexed.
      </p>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function PredictionsPage() {
  const { theme } = useTheme();
  const ui = usePredictionUI();
  const { setBreadcrumbItems } = useBreadcrumb();

  const [sportTypes, setSportTypes] = useState([]);
  const [supportedSports, setSupportedSports] = useState(null); // null = loading, { pro: [], kynaix: [] }
  const [selectedSport, setSelectedSport] = useState(null); // { id, name }

  const [tab, setTab] = useState('pro');
  const [gameType, setGameType] = useState('singles'); // 'singles' | 'doubles'

  // Pro tab state
  const [proP1, setProP1] = useState(null);
  const [proP2, setProP2] = useState(null);
  const [proP3, setProP3] = useState(null);
  const [proP4, setProP4] = useState(null);

  // Kynaix tab state
  const [kP1, setKP1] = useState(null);
  const [kP2, setKP2] = useState(null);
  const [kP3, setKP3] = useState(null);
  const [kP4, setKP4] = useState(null);

  // Shared result state
  const [predicting, setPredicting] = useState(false);
  const [result, setResult] = useState(null);
  const [resultPlayer1, setResultPlayer1] = useState(null);
  const [resultPlayer2, setResultPlayer2] = useState(null);
  const [resultPlayer1Partner, setResultPlayer1Partner] = useState(null);
  const [resultPlayer2Partner, setResultPlayer2Partner] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Home', path: '/' }, { label: 'Predictions' }]);
    Promise.all([
      sportTypeAPI.getAll().catch(() => []),
      predictionsAPI.getSupportedSports().catch(() => ({ pro: [], kynaix: [] })),
    ]).then(([types, supported]) => {
      setSportTypes(types || []);
      setSupportedSports(supported);
    });
  }, [setBreadcrumbItems]);

  const resetResult = () => {
    setResult(null);
    setResultPlayer1(null);
    setResultPlayer2(null);
    setResultPlayer1Partner(null);
    setResultPlayer2Partner(null);
  };

  const resetPlayers = () => {
    setProP1(null); setProP2(null); setProP3(null); setProP4(null);
    setKP1(null); setKP2(null); setKP3(null); setKP4(null);
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    resetResult();
    resetPlayers();
  };

  const handleGameTypeChange = (type) => {
    setGameType(type);
    resetResult();
    resetPlayers();
  };

  const runPrediction = async ({ p1, p2, p3, p4, sportId }) => {
    if (!p1 || !p2) return;
    if (p1.id === p2.id) { toast.error('Select two different players'); return; }
    setResult(null);
    // Singles: p1 vs p2. Doubles: Team A (p1+p2) vs Team B (p3+p4).
    setResultPlayer1(p1);
    setResultPlayer2(p3 || p2);
    setResultPlayer1Partner(p3 ? p2 : null);
    setResultPlayer2Partner(p3 ? p4 : null);
    setPredicting(true);
    if (resultRef.current) resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    try {
      const res = await predictionsAPI.predict({
        sportId,
        // Backend contract: player1Id=TeamA-P1, player2Id=TeamB-P1, player3Id=TeamA-P2, player4Id=TeamB-P2
        player1Id: p1.id,
        player2Id: p3 ? p3.id : p2.id,
        ...(p3 && { player3Id: p2.id }),
        ...(p4 && { player4Id: p4.id }),
      });
      setResult(res);
      setTimeout(() => {
        if (resultRef.current) resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (err) {
      if (err?.status === 422) {
        toast.error(err?.data?.error || 'One or more players have no match history in the model.');
      } else {
        toast.error(err?.message || 'Prediction failed. Try again.');
      }
      setResult(null);
      setResultPlayer1(null);
      setResultPlayer2(null);
    } finally {
      setPredicting(false);
    }
  };

  const proIds    = supportedSports?.pro    ?? [];
  const kynaixIds = supportedSports?.kynaix ?? [];
  const allIds    = [...new Set([...proIds, ...kynaixIds])];

  // A sport pill gets "Soon" only if it has no indexing in either source
  const isSportSupportedAnywhere = (id) => supportedSports === null || allIds.includes(id);
  // Per-tab support check for the selected sport
  const isProSupported    = !selectedSport || supportedSports === null || proIds.includes(selectedSport.id);
  const isKynaixSupported = !selectedSport || supportedSports === null || kynaixIds.includes(selectedSport.id);
  const isTennis = selectedSport && (selectedSport.name || '').toLowerCase() === 'tennis';

  return (
    <div className={`${theme.background} min-h-screen`}>
      <Helmet>
        <title>Match Predictions — Kynaix Sports</title>
        <meta name="description" content="AI-powered match predictions for professional and Kynaix sports matches." />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-28 space-y-6">

        {/* ── Page header ── */}
        <div className="text-center space-y-3 py-4">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-blue-500/20 blur-xl" />
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-xl shadow-blue-500/30">
                <Zap size={28} className="text-white" />
              </div>
            </div>
          </div>
          <div>
            <h1 className={`text-3xl font-black ${ui.title} tracking-tight`}>Match Oracle</h1>
            <p className={`text-sm ${ui.label} mt-1.5 flex items-center justify-center gap-1.5`}>
              <Sparkles size={12} className="text-amber-400" />
              Neural network predictions powered by match history
            </p>
          </div>
        </div>

        {/* ── STEP 1: Sport selection ── */}
        <div className={`p-5 rounded-2xl ${ui.panel} border ${ui.border} space-y-4`}>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-xs font-black text-white flex-shrink-0">1</span>
            <p className={`text-sm font-bold ${ui.dim} uppercase tracking-widest`}>Select Sport</p>
          </div>
          {sportTypes.length === 0 ? (
            <div className="flex gap-2 flex-wrap">
              {['Tennis', 'Basketball', 'Volleyball', 'Pickleball'].map(s => (
                <div key={s} className={`h-10 w-28 rounded-xl ${ui.surface} animate-pulse`} />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sportTypes.map(s => {
                const name = s.sportName || s.sport_name || '';
                const isSelected = selectedSport?.id === s.id;
                const isSupported = isSportSupportedAnywhere(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => handleSportSelect({ id: s.id, name })}
                    className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      isSelected
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 scale-105'
                        : `${ui.surface} border ${ui.border} ${ui.label} ${ui.hoverTitle} ${ui.hoverBorder} ${ui.hoverSurface}`
                    }`}
                  >
                    <span className="text-base">{sportEmoji(name)}</span>
                    {name}
                    {!isSupported && (
                      <span className={`text-[9px] font-black ${ui.surfaceHi} ${ui.label} px-1.5 py-0.5 rounded-full uppercase tracking-wider`}>Soon</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── STEP 2 onwards — shown after sport is selected ── */}
        {selectedSport && (
          <>
            {/* ── Singles / Doubles toggle ── */}
            <div className="flex gap-2">
              {['singles', 'doubles'].map(gt => (
                <button
                  key={gt}
                  onClick={() => handleGameTypeChange(gt)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
                    gameType === gt
                      ? `${ui.activeSurface} ${ui.title} border ${ui.borderHi}`
                      : `${ui.faint} ${ui.hoverDim} border ${ui.borderSoft}`
                  }`}
                >
                  <span>{gt === 'singles' ? '👤' : '👥'}</span>
                  {gt}
                </button>
              ))}
            </div>

            {/* ── Tabs ── */}
            <div className={`flex gap-2 p-1 rounded-xl ${ui.surface} border ${ui.border}`}>
              <button
                onClick={() => { setTab('pro'); resetResult(); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  tab === 'pro'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                    : `${ui.label} ${ui.hoverDim}`
                }`}
              >
                <Trophy size={15} />
                Pro Matches
              </button>
              <button
                onClick={() => { setTab('kynaix'); resetResult(); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  tab === 'kynaix'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25'
                    : `${ui.label} ${ui.hoverDim}`
                }`}
              >
                <Users size={15} />
                Kynaix Matches
              </button>
            </div>

            {/* ── PRO TAB ── */}
            {tab === 'pro' && (
              <div className="space-y-5">
                {!isProSupported ? (
                  <ComingSoonCard sport={selectedSport} context="Pro" />
                ) : isTennis ? (
                  <>
                    {/* Featured rivalries */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Star size={14} className="text-amber-400" />
                        <p className={`text-xs font-bold ${ui.label} uppercase tracking-widest`}>Featured Rivalries</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {FEATURED_MATCHUPS.map(m => (
                          <button
                            key={m.id}
                            onClick={() => runPrediction({ p1: m.player1, p2: m.player2, sportId: m.sportId })}
                            className={`group flex items-center gap-4 p-4 rounded-xl ${ui.surface} border ${ui.borderSoft} ${ui.hoverBorder} ${ui.hoverSurface} transition-all text-left active:scale-[0.98]`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <PlayerAvatar name={m.player1.name} id={m.player1.id} size="sm" />
                              <div className={`w-5 h-5 rounded-full ${ui.surfaceHi} flex items-center justify-center flex-shrink-0`}>
                                <span className={`text-[9px] font-black ${ui.label}`}>VS</span>
                              </div>
                              <PlayerAvatar name={m.player2.name} id={m.player2.id} size="sm" />
                              <div className="flex-1 min-w-0 ml-1">
                                <p className={`text-xs font-bold ${ui.title} truncate`}>{m.player1.name.split(' ').pop()} vs {m.player2.name.split(' ').pop()}</p>
                                <p className={`text-[10px] ${ui.faint} truncate`}>{m.label}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white uppercase tracking-wider ${m.badgeColor}`}>{m.badge}</span>
                              <Zap size={13} className={`${ui.faint} group-hover:text-blue-400 transition-colors`} />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3">
                      <div className={`flex-1 h-px ${ui.divider}`} />
                      <span className={`text-xs ${ui.faint} font-medium`}>or build your own</span>
                      <div className={`flex-1 h-px ${ui.divider}`} />
                    </div>

                    {/* Custom matchup builder */}
                    <div className={`p-5 rounded-2xl ${ui.panel} border ${ui.borderSoft} space-y-4`}>
                      <p className={`text-xs font-bold ${ui.label} uppercase tracking-widest flex items-center gap-1.5`}>
                        <TrendingUp size={13} />
                        Custom Matchup
                      </p>
                      {gameType === 'doubles' ? (
                        <div className="space-y-3">
                          <p className={`text-[10px] ${ui.faint} uppercase tracking-widest font-semibold`}>Team A</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <PlayerSelect label="Player 1" value={proP1} onChange={p => { setProP1(p); resetResult(); }} options={ATP_PLAYERS.filter(p => ![proP2?.id,proP3?.id,proP4?.id].includes(p.id))} placeholder="Select player 1" />
                            <PlayerSelect label="Partner"  value={proP2} onChange={p => { setProP2(p); resetResult(); }} options={ATP_PLAYERS.filter(p => ![proP1?.id,proP3?.id,proP4?.id].includes(p.id))} placeholder="Select partner" />
                          </div>
                          <p className={`text-[10px] ${ui.faint} uppercase tracking-widest font-semibold pt-1`}>Team B</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <PlayerSelect label="Player 3" value={proP3} onChange={p => { setProP3(p); resetResult(); }} options={ATP_PLAYERS.filter(p => ![proP1?.id,proP2?.id,proP4?.id].includes(p.id))} placeholder="Select player 3" />
                            <PlayerSelect label="Partner"  value={proP4} onChange={p => { setProP4(p); resetResult(); }} options={ATP_PLAYERS.filter(p => ![proP1?.id,proP2?.id,proP3?.id].includes(p.id))} placeholder="Select partner" />
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <PlayerSelect label="Player 1" value={proP1} onChange={p => { setProP1(p); resetResult(); }} options={ATP_PLAYERS.filter(p => p.id !== proP2?.id)} placeholder="Select player 1" />
                          <PlayerSelect label="Player 2" value={proP2} onChange={p => { setProP2(p); resetResult(); }} options={ATP_PLAYERS.filter(p => p.id !== proP1?.id)} placeholder="Select player 2" />
                        </div>
                      )}
                      <button
                        onClick={() => runPrediction({ p1: proP1, p2: proP2, p3: proP3, p4: proP4, sportId: selectedSport.id })}
                        disabled={!proP1 || !proP2 || (gameType === 'doubles' && (!proP3 || !proP4)) || predicting}
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-all shadow-lg shadow-blue-500/20 disabled:shadow-none flex items-center justify-center gap-2"
                      >
                        <Zap size={15} />
                        Predict Match
                      </button>
                    </div>
                  </>
                ) : (
                  <ComingSoonCard sport={selectedSport} context="Pro" />
                )}
              </div>
            )}

            {/* ── KYNAIX TAB ── */}
            {tab === 'kynaix' && (
              <div className="space-y-5">
                {!isKynaixSupported ? (
                  <ComingSoonCard sport={selectedSport} context="Kynaix" />
                ) : (
                <div className={`p-5 rounded-2xl ${ui.panel} border ${ui.borderSoft} space-y-4`}>
                  <p className={`text-xs font-bold ${ui.label} uppercase tracking-widest flex items-center gap-1.5`}>
                    <Shield size={13} />
                    Kynaix {selectedSport.name} Match Prediction
                  </p>

                  {gameType === 'doubles' ? (
                    <div className="space-y-3">
                      <p className={`text-[10px] ${ui.faint} uppercase tracking-widest font-semibold`}>Team A</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <KynaixPlayerSearch label="Player 1" value={kP1} onChange={p => { setKP1(p); resetResult(); }} />
                        <KynaixPlayerSearch label="Partner"  value={kP2} onChange={p => { setKP2(p); resetResult(); }} />
                      </div>
                      <p className={`text-[10px] ${ui.faint} uppercase tracking-widest font-semibold pt-1`}>Team B</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <KynaixPlayerSearch label="Player 3" value={kP3} onChange={p => { setKP3(p); resetResult(); }} />
                        <KynaixPlayerSearch label="Partner"  value={kP4} onChange={p => { setKP4(p); resetResult(); }} />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <KynaixPlayerSearch label="Player 1" value={kP1} onChange={p => { setKP1(p); resetResult(); }} />
                      <KynaixPlayerSearch label="Player 2" value={kP2} onChange={p => { setKP2(p); resetResult(); }} />
                    </div>
                  )}

                  <div className={`pt-1 border-t ${ui.borderSoft}`}>
                    <p className={`text-xs ${ui.faint} mb-3 flex items-center gap-1.5`}>
                      <Sparkles size={10} className="text-amber-400/60" />
                      Prediction accuracy is higher for players with more match history on the platform
                    </p>
                    <button
                      onClick={() => runPrediction({ p1: kP1, p2: kP2, p3: kP3, p4: kP4, sportId: selectedSport.id })}
                      disabled={!kP1 || !kP2 || (gameType === 'doubles' && (!kP3 || !kP4)) || predicting}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-purple-900 disabled:to-pink-900 disabled:text-white/30 text-white font-bold text-sm transition-all shadow-lg shadow-purple-500/20 disabled:shadow-none flex items-center justify-center gap-2"
                    >
                      <Zap size={15} />
                      Predict Match
                    </button>
                  </div>
                </div>
                )}
              </div>
            )}

            {/* ── Result area ── */}
            <div ref={resultRef}>
              {predicting && (
                <PredictingLoader player1={resultPlayer1} player2={resultPlayer2} />
              )}
              {result && !predicting && (
                <PredictionResult
                  result={result}
                  player1={resultPlayer1}
                  player2={resultPlayer2}
                  player1Partner={resultPlayer1Partner}
                  player2Partner={resultPlayer2Partner}
                  onReset={resetResult}
                  sportName={selectedSport?.name}
                />
              )}
            </div>

            {/* ── Footer note ── */}
            {!predicting && !result && (
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
                <Sparkles size={14} className="text-amber-400/70 mt-0.5 flex-shrink-0" />
                <p className={`text-xs ${ui.faint} leading-relaxed`}>
                  Predictions are generated by a neural network trained on historical match data.
                  Professional predictions use ATP tour data (2015–2025). Kynaix predictions improve as more matches are recorded on the platform.
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Prompt when no sport selected ── */}
        {!selectedSport && sportTypes.length > 0 && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="text-5xl">🏅</div>
            <p className={`${ui.label} text-sm`}>Pick a sport above to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
